import React, { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { useAuth } from '@/features/auth/AuthContext';
import { saveWalkthroughState } from './api/walkthroughApi';
import {
    getWalkthrough,
    isEphemeralWalkthrough,
    stepIdAt,
    stepIndexOf,
    WALKTHROUGH_STATUS,
} from './registry';
import { registerContextPublisher } from './walkthroughContextBus';

/**
 * App-wide walkthrough state: which walkthrough the user chose / is running,
 * where the pointer is, and the live `ctx` the host pages publish for the
 * step predicates. Mounted once (see `AppRoutes`); the page that hosts a
 * walkthrough mounts a `WalkthroughRunner` that renders the coach marks.
 *
 * Persistence: `user.preferences.walkthroughs` from the auth bootstrap seeds
 * `states`; every status/pointer change is saved through
 * `POST /users/set-preferences` (best effort — local state is authoritative).
 * Ephemeral walkthroughs (the demo tour) are tracked locally only: nothing is
 * written and any stale bootstrap entry for them is ignored.
 */
export const WalkthroughContext = createContext(null);

const INITIAL = {
    /** `{ [walkthroughId]: { status, step_id } }` */
    states: {},
    /** Ids whose state was changed locally — bootstrap must not overwrite them. */
    touched: {},
    active: null,
    stepIndex: 0,
    skippedIds: [],
    completedIds: [],
    /** Steps passed over because they did not apply; they return if they apply again on their page. */
    deferredIds: [],
    /**
     * While the user walks back with Back: the index of the step Back was
     * pressed on. Every applicable step before it is shown again (even if
     * its action is done) until the pointer is back there; `null` otherwise.
     */
    revisitUntil: null,
    ctx: {},
    /** Id of the step whose coach mark is on screen right now (published by the runner), or null. */
    visibleStepId: null,
    /**
     * True once the signed-in user's bootstrap preferences were applied (or
     * the user loaded without any). Until then "never seen" cannot be told
     * from "not loaded yet", so runners must not auto-start on it.
     */
    seeded: false,
};

/** Appends unseen ids; returns `list` itself when nothing is new so callers can compare by identity. */
const mergeIds = (list, ids) => {
    const fresh = (ids || []).filter((id) => !list.includes(id));
    return fresh.length ? [...list, ...fresh] : list;
};

export function walkthroughReducer(state, action) {
    switch (action.type) {
        case 'seed': {
            const incoming = action.states || {};
            const states = { ...state.states };
            for (const [id, value] of Object.entries(incoming)) {
                if (isEphemeralWalkthrough(getWalkthrough(id))) continue;
                if (!state.touched[id] && value && typeof value === 'object') states[id] = value;
            }
            return { ...state, states, seeded: true };
        }
        case 'choose': {
            const { id, status } = action;
            const inProgress = status === WALKTHROUGH_STATUS.inProgress;
            return {
                ...state,
                states: { ...state.states, [id]: { status, step_id: null } },
                touched: { ...state.touched, [id]: true },
                active: inProgress ? id : state.active === id ? null : state.active,
                stepIndex: inProgress ? 0 : state.stepIndex,
                skippedIds: inProgress ? [] : state.skippedIds,
                completedIds: inProgress ? [] : state.completedIds,
                deferredIds: inProgress ? [] : state.deferredIds,
                revisitUntil: inProgress ? null : state.revisitUntil,
            };
        }
        case 'start': {
            const { id, stepIndex } = action;
            const stepId = action.stepId ?? null;
            return {
                ...state,
                states: { ...state.states, [id]: { status: WALKTHROUGH_STATUS.inProgress, step_id: stepId } },
                touched: { ...state.touched, [id]: true },
                active: id,
                stepIndex,
                skippedIds: [],
                completedIds: [],
                deferredIds: [],
                revisitUntil: null,
                visibleStepId: null,
            };
        }
        case 'advance': {
            if (!state.active) return state;
            const stepIndex = action.stepIndex;
            const skippedIds = mergeIds(state.skippedIds, action.skipped);
            const completedIds = mergeIds(state.completedIds, action.completed);
            const deferredIds = mergeIds(state.deferredIds, action.deferred);
            // Back where Back was pressed (or past it): the walk back is over.
            const revisitUntil =
                state.revisitUntil !== null && stepIndex >= state.revisitUntil ? null : state.revisitUntil;
            if (
                stepIndex === state.stepIndex &&
                skippedIds === state.skippedIds &&
                completedIds === state.completedIds &&
                deferredIds === state.deferredIds &&
                revisitUntil === state.revisitUntil
            ) {
                return state;
            }
            return {
                ...state,
                states: {
                    ...state.states,
                    [state.active]: { status: WALKTHROUGH_STATUS.inProgress, step_id: action.stepId ?? null },
                },
                touched: { ...state.touched, [state.active]: true },
                stepIndex,
                skippedIds,
                completedIds,
                deferredIds,
                revisitUntil,
            };
        }
        case 'back': {
            if (!state.active) return state;
            const stepIndex = action.stepIndex;
            if (!(stepIndex >= 0) || stepIndex >= state.stepIndex) return state;
            return {
                ...state,
                states: {
                    ...state.states,
                    [state.active]: { status: WALKTHROUGH_STATUS.inProgress, step_id: action.stepId ?? null },
                },
                touched: { ...state.touched, [state.active]: true },
                stepIndex,
                // A second Back while already walking back keeps the farther mark.
                revisitUntil: Math.max(state.revisitUntil ?? -1, state.stepIndex),
            };
        }
        case 'end': {
            const id = action.id || state.active;
            if (!id) return state;
            return {
                ...state,
                states: { ...state.states, [id]: { status: action.status, step_id: null } },
                touched: { ...state.touched, [id]: true },
                active: state.active === id ? null : state.active,
                visibleStepId: state.active === id ? null : state.visibleStepId,
            };
        }
        case 'show': {
            const visibleStepId = action.stepId ?? null;
            return visibleStepId === state.visibleStepId ? state : { ...state, visibleStepId };
        }
        case 'publish': {
            const partial = action.partial || {};
            let changed = false;
            const ctx = { ...state.ctx };
            for (const [key, value] of Object.entries(partial)) {
                if (ctx[key] !== value) {
                    ctx[key] = value;
                    changed = true;
                }
            }
            return changed ? { ...state, ctx } : state;
        }
        default:
            return state;
    }
}

export function WalkthroughProvider({ children }) {
    const auth = useAuth();
    const bootPreferences = auth?.user?.preferences?.walkthroughs;
    const [state, dispatch] = useReducer(walkthroughReducer, INITIAL);
    const stateRef = useRef(state);
    stateRef.current = state;

    // Seed once the user is known. A user without saved walkthroughs still
    // counts as seeded: that is exactly the "never seen" case.
    const authLoading = Boolean(auth?.loading);
    const hasUser = Boolean(auth?.user);
    useEffect(() => {
        if (authLoading || !hasUser) return;
        dispatch({ type: 'seed', states: bootPreferences && typeof bootPreferences === 'object' ? bootPreferences : {} });
    }, [authLoading, hasUser, bootPreferences]);

    const persist = useCallback((id, status, stepId) => {
        if (isEphemeralWalkthrough(getWalkthrough(id))) return;
        saveWalkthroughState(id, { status, stepId });
    }, []);

    const choose = useCallback(
        (id, status) => {
            dispatch({ type: 'choose', id, status });
            persist(id, status, null);
        },
        [persist]
    );

    /**
     * Run walkthrough `id`, resuming from its saved `step_id`. `restart: true`
     * (the "View Guided Tour" button) always begins at the first step.
     */
    const start = useCallback(
        (id, { restart = false } = {}) => {
            const definition = getWalkthrough(id);
            if (!definition) return;
            const saved = stateRef.current.states[id];
            const stepIndex = restart ? 0 : stepIndexOf(definition, saved?.step_id);
            const stepId = stepIdAt(definition, stepIndex);
            dispatch({ type: 'start', id, stepIndex, stepId });
            if (saved?.status !== WALKTHROUGH_STATUS.inProgress || saved?.step_id !== stepId) {
                persist(id, WALKTHROUGH_STATUS.inProgress, stepId);
            }
        },
        [persist]
    );

    const advance = useCallback(
        ({ stepIndex, skipped, completed, deferred }) => {
            const { active, stepIndex: prevIndex } = stateRef.current;
            if (!active) return;
            const stepId = stepIdAt(getWalkthrough(active), stepIndex);
            dispatch({ type: 'advance', stepIndex, skipped, completed, deferred, stepId });
            if (stepIndex !== prevIndex) persist(active, WALKTHROUGH_STATUS.inProgress, stepId);
        },
        [persist]
    );

    const completeStep = useCallback(() => {
        const { active, stepIndex } = stateRef.current;
        if (!active) return;
        const step = getWalkthrough(active)?.steps[stepIndex];
        advance({ stepIndex: stepIndex + 1, completed: step ? [step.id] : [] });
    }, [advance]);

    const skipStep = useCallback(() => {
        const { active, stepIndex } = stateRef.current;
        if (!active) return;
        const step = getWalkthrough(active)?.steps[stepIndex];
        advance({ stepIndex: stepIndex + 1, skipped: step ? [step.id] : [] });
    }, [advance]);

    /**
     * Back: put the pointer on the earlier step `stepIndex` (the runner picks
     * it, see `previousStepIndex`) and remember where Back was pressed so the
     * steps walked over are shown again one by one on the way forward.
     */
    const stepBack = useCallback(
        (stepIndex) => {
            const { active, stepIndex: prevIndex } = stateRef.current;
            if (!active || !(stepIndex >= 0) || stepIndex >= prevIndex) return;
            const stepId = stepIdAt(getWalkthrough(active), stepIndex);
            dispatch({ type: 'back', stepIndex, stepId });
            persist(active, WALKTHROUGH_STATUS.inProgress, stepId);
        },
        [persist]
    );

    const end = useCallback(
        (status, id) => {
            const target = id || stateRef.current.active;
            if (!target) return;
            dispatch({ type: 'end', id: target, status });
            persist(target, status, null);
        },
        [persist]
    );

    const publishContext = useCallback((partial) => {
        if (partial && typeof partial === 'object') dispatch({ type: 'publish', partial });
    }, []);

    /** The runner reports which step's coach mark is on screen (`null` when none). */
    const setVisibleStep = useCallback((stepId) => dispatch({ type: 'show', stepId }), []);

    // Non-component code (e.g. antd column builders) publishes through the bus.
    useEffect(() => registerContextPublisher(publishContext), [publishContext]);

    const value = useMemo(
        () => ({
            states: state.states,
            active: state.active,
            stepIndex: state.stepIndex,
            skippedIds: state.skippedIds,
            completedIds: state.completedIds,
            deferredIds: state.deferredIds,
            revisitUntil: state.revisitUntil,
            ctx: state.ctx,
            visibleStepId: state.visibleStepId,
            seeded: state.seeded,
            definition: state.active ? getWalkthrough(state.active) : null,
            choose,
            start,
            advance,
            completeStep,
            skipStep,
            stepBack,
            end,
            publishContext,
            setVisibleStep,
        }),
        [state, choose, start, advance, completeStep, skipStep, stepBack, end, publishContext, setVisibleStep]
    );

    return <WalkthroughContext.Provider value={value}>{children}</WalkthroughContext.Provider>;
}

export default WalkthroughProvider;
